import { useEffect, useState } from "react";
import { Paperclip, Upload, Download, Trash2, FileText, Music2 } from "lucide-react";
import { http, errMsg, fmtDateTime, downloadFile } from "../api";
import AudioPlayer from "./AudioPlayer";

const KINDS = [["script", "Script (PDF / DOCX / TXT)"], ["reference", "Reference audio"], ["other", "Other"]];

export default function OrderFiles({ orderId, canUpload }) {
  const [files, setFiles] = useState([]);
  const [kind, setKind] = useState("script");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = () => http.get(`/orders/${orderId}/files`).then(r => setFiles(r.data)).catch(() => {});
  useEffect(() => { load(); }, [orderId]);

  const upload = async () => {
    if (!file) return;
    setBusy(true); setErr("");
    const fd = new FormData(); fd.append("kind", kind); fd.append("note", note); fd.append("file", file);
    try { await http.post(`/orders/${orderId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setFile(null); setNote(""); load(); }
    catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async f => { await http.delete(`/orders/${orderId}/files/${f.id}`); load(); };

  return (
    <div data-testid={`order-files-${orderId}`}>
      <div className="section-title"><span><Paperclip size={12} style={{ display: "inline", marginRight: 4 }} />PROJECT FILES ({files.length})</span></div>
      {files.length === 0 && <p style={{ fontSize: 12.5, color: "var(--muted)" }}>No script or reference files yet.</p>}
      {files.map(f => (
        <div key={f.id} className="list-row" style={{ padding: 10, marginBottom: 6 }} data-testid={`order-file-${f.id}`}>
          {f.kind === "reference" ? <Music2 size={16} color="var(--primary)" /> : <FileText size={16} color="var(--primary)" />}
          <div className="list-row-info"><strong>{f.filename}</strong><p>{KINDS.find(k => k[0] === f.kind)?.[1] || f.kind}{f.note ? ` · ${f.note}` : ""} · {f.uploader_name} · {fmtDateTime(f.created_at)}</p></div>
          {f.kind === "reference" && f.content_type?.startsWith("audio") && <AudioPlayer id={f.id} compact />}
          <button className="ghost" onClick={() => downloadFile(`/files/${f.id}`, {}, f.filename)} data-testid={`order-file-download-${f.id}`}><Download size={13} /></button>
          {canUpload && <button className="pill-action reject" onClick={() => remove(f)} data-testid={`order-file-delete-${f.id}`}><Trash2 size={13} /></button>}
        </div>
      ))}
      {canUpload && (
        <div style={{ padding: 10, background: "var(--surface-2)", borderRadius: 8, marginTop: 6 }}>
          <div className="field-row">
            <div><label>File type</label><select value={kind} onChange={e => setKind(e.target.value)} data-testid="order-file-kind">{KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
            <div><label>Note for artist (optional)</label><input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Read pages 2–5 only" data-testid="order-file-note" /></div>
          </div>
          <label className="dropzone" style={{ padding: 12, marginTop: 6 }}>
            <input type="file" accept={kind === "reference" ? "audio/*" : ".pdf,.doc,.docx,.txt,.rtf,audio/*,application/pdf"} onChange={e => setFile(e.target.files[0])} data-testid="order-file-input" />
            <p>{file ? file.name : kind === "reference" ? "Select reference audio (how you want it to sound)" : "Select script PDF / DOCX / TXT"}</p>
          </label>
          {err && <div className="notice error" style={{ marginTop: 8 }}>{err}</div>}
          <button className="primary" style={{ marginTop: 8 }} onClick={upload} disabled={busy || !file} data-testid="order-file-upload"><Upload size={13} /> {busy ? "Uploading…" : "Upload file"}</button>
        </div>
      )}
    </div>
  );
}
